import type { LibrarySketch } from './types';

export const particleSketches: LibrarySketch[] = [
  {
    id: 'particles-flocking-boids',
    name: 'Flocking Boids',
    description: 'Classic boids flocking simulation with separation, alignment, and cohesion behaviors creating emergent swarming patterns.',
    category: 'particles',
    tags: ['boids', 'flocking', 'swarm', 'emergent', 'simulation'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'count', type: 'int', min: 20, max: 300, default: 150, label: 'Boid Count' },
      { name: 'maxSpeed', type: 'float', min: 1.0, max: 6.0, default: 3.0, label: 'Max Speed' },
      { name: 'perceptionRadius', type: 'float', min: 20, max: 100, default: 50, label: 'Perception' },
      { name: 'trailAlpha', type: 'int', min: 5, max: 50, default: 20, label: 'Trail' },
    ],
    code: `// @param int count 20 300 150
// @param float maxSpeed 1.0 6.0 3.0
// @param float perceptionRadius 20 100 50
// @param int trailAlpha 5 50 20

let boids = [];
function setup() {
  createCanvas(genie.width, genie.height);
  const n = genie.params.count ?? 150;
  for (let i = 0; i < n; i++) {
    boids.push({
      x: random(width), y: random(height),
      vx: random(-2, 2), vy: random(-2, 2)
    });
  }
}
function steer(b, tx, ty, factor) {
  b.vx += tx * factor; b.vy += ty * factor;
}
function draw() {
  const ms = genie.params.maxSpeed ?? 3.0;
  const pr = genie.params.perceptionRadius ?? 50;
  const ta = genie.params.trailAlpha ?? 20;
  fill(0, ta); noStroke(); rect(0, 0, width, height);
  stroke(180, 220, 255); strokeWeight(2);
  for (const b of boids) {
    let sx = 0, sy = 0, ax = 0, ay = 0, cx = 0, cy = 0, cnt = 0;
    for (const o of boids) {
      if (o === b) continue;
      const d = dist(b.x, b.y, o.x, o.y);
      if (d < pr && d > 0) {
        sx += (b.x - o.x) / d; sy += (b.y - o.y) / d;
        ax += o.vx; ay += o.vy;
        cx += o.x; cy += o.y;
        cnt++;
      }
    }
    if (cnt > 0) {
      steer(b, sx / cnt, sy / cnt, 0.05);
      steer(b, ax / cnt - b.vx, ay / cnt - b.vy, 0.03);
      steer(b, cx / cnt - b.x, cy / cnt - b.y, 0.005);
    }
    const spd = sqrt(b.vx * b.vx + b.vy * b.vy);
    if (spd > ms) { b.vx = (b.vx / spd) * ms; b.vy = (b.vy / spd) * ms; }
    b.x += b.vx; b.y += b.vy;
    if (b.x < 0) b.x = width; if (b.x > width) b.x = 0;
    if (b.y < 0) b.y = height; if (b.y > height) b.y = 0;
    point(b.x, b.y);
  }
}`,
  },

  {
    id: 'particles-attraction-repulsion',
    name: 'Attract & Repel',
    description: 'Particles are attracted to the mouse position and repelled when they get too close, creating a dynamic force field.',
    category: 'particles',
    tags: ['attraction', 'repulsion', 'mouse', 'force', 'interactive'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'count', type: 'int', min: 50, max: 2000, default: 500, label: 'Particles' },
      { name: 'attractForce', type: 'float', min: 0.01, max: 0.5, default: 0.1, label: 'Attract' },
      { name: 'repelRadius', type: 'float', min: 20, max: 150, default: 60, label: 'Repel Radius' },
      { name: 'friction', type: 'float', min: 0.9, max: 0.999, default: 0.97, label: 'Friction' },
    ],
    code: `// @param int count 50 2000 500
// @param float attractForce 0.01 0.5 0.1
// @param float repelRadius 20 150 60
// @param float friction 0.9 0.999 0.97

let pts = [];
function setup() {
  createCanvas(genie.width, genie.height);
  const n = genie.params.count ?? 500;
  for (let i = 0; i < n; i++) {
    pts.push({ x: random(width), y: random(height), vx: 0, vy: 0 });
  }
}
function draw() {
  background(0, 25);
  const af = genie.params.attractForce ?? 0.1;
  const rr = genie.params.repelRadius ?? 60;
  const fr = genie.params.friction ?? 0.97;
  const mx = mouseX || width / 2;
  const my = mouseY || height / 2;
  noStroke();
  for (const p of pts) {
    const dx = mx - p.x, dy = my - p.y;
    const d = sqrt(dx * dx + dy * dy) + 0.1;
    if (d > rr) {
      p.vx += (dx / d) * af;
      p.vy += (dy / d) * af;
    } else {
      p.vx -= (dx / d) * af * 2;
      p.vy -= (dy / d) * af * 2;
    }
    p.vx *= fr; p.vy *= fr;
    p.x += p.vx; p.y += p.vy;
    if (p.x < 0) { p.x = 0; p.vx *= -0.5; }
    if (p.x > width) { p.x = width; p.vx *= -0.5; }
    if (p.y < 0) { p.y = 0; p.vy *= -0.5; }
    if (p.y > height) { p.y = height; p.vy *= -0.5; }
    const spd = sqrt(p.vx * p.vx + p.vy * p.vy);
    const hue = map(spd, 0, 5, 180, 0);
    colorMode(HSB, 360, 100, 100, 100);
    fill(hue, 80, 100, 60);
    circle(p.x, p.y, 3);
  }
}`,
  },

  {
    id: 'particles-fireworks',
    name: 'Fireworks',
    description: 'Colorful firework explosions that launch, burst, and fade with gravity and trail effects.',
    category: 'particles',
    tags: ['fireworks', 'explosion', 'celebration', 'gravity', 'colorful'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'launchRate', type: 'float', min: 0.01, max: 0.15, default: 0.05, label: 'Launch Rate' },
      { name: 'particlesPerBurst', type: 'int', min: 20, max: 200, default: 80, label: 'Burst Size' },
      { name: 'gravity', type: 'float', min: 0.02, max: 0.2, default: 0.06, label: 'Gravity' },
      { name: 'trailLength', type: 'int', min: 5, max: 40, default: 15, label: 'Trail' },
    ],
    code: `// @param float launchRate 0.01 0.15 0.05
// @param int particlesPerBurst 20 200 80
// @param float gravity 0.02 0.2 0.06
// @param int trailLength 5 40 15

let rockets = [], sparks = [];
function setup() {
  createCanvas(genie.width, genie.height);
  colorMode(HSB, 360, 100, 100, 100);
}
function draw() {
  const g = genie.params.gravity ?? 0.06;
  const lr = genie.params.launchRate ?? 0.05;
  const bst = genie.params.particlesPerBurst ?? 80;
  background(0, genie.params.trailLength ?? 15);
  if (random() < lr) {
    // Launch speed from the canvas height so every rocket bursts on screen (apex = v² / 2g).
    rockets.push({ x: random(width), y: height, vy: -sqrt(2 * g * height * random(0.5, 0.85)), hue: random(360) });
  }
  for (let i = rockets.length - 1; i >= 0; i--) {
    const r = rockets[i];
    stroke(r.hue, 80, 100);
    strokeWeight(3); point(r.x, r.y);
    r.y += r.vy; r.vy += g;
    if (r.vy >= 0) {
      for (let j = 0; j < bst; j++) {
        const a = random(TWO_PI), spd = random(1, 5);
        sparks.push({
          x: r.x, y: r.y,
          vx: cos(a) * spd, vy: sin(a) * spd,
          hue: r.hue + random(-20, 20), life: 1
        });
      }
      rockets.splice(i, 1);
    }
  }
  noStroke();
  for (let i = sparks.length - 1; i >= 0; i--) {
    const s = sparks[i];
    fill(s.hue % 360, 80, 100, s.life * 100);
    circle(s.x, s.y, s.life * 4);
    s.x += s.vx; s.y += s.vy; s.vy += g * 0.5;
    s.vx *= 0.98; s.vy *= 0.98;
    s.life -= 0.015;
    if (s.life <= 0) sparks.splice(i, 1);
  }
}`,
  },

  {
    id: 'particles-trails-ribbon',
    name: 'Ribbon Trails',
    description: 'Smooth ribbon trails follow the mouse with flowing, organic motion and gradient coloring.',
    category: 'particles',
    tags: ['ribbon', 'trails', 'mouse', 'smooth', 'organic'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'ribbonCount', type: 'int', min: 1, max: 10, default: 5, label: 'Ribbons' },
      { name: 'maxLength', type: 'int', min: 20, max: 200, default: 80, label: 'Trail Length' },
      { name: 'thickness', type: 'float', min: 1, max: 20, default: 8, label: 'Thickness' },
      { name: 'speed', type: 'float', min: 0.02, max: 0.3, default: 0.1, label: 'Follow Speed' },
    ],
    code: `// @param int ribbonCount 1 10 5
// @param int maxLength 20 200 80
// @param float thickness 1 20 8
// @param float speed 0.02 0.3 0.1

let ribbons = [];
function setup() {
  createCanvas(genie.width, genie.height);
  const n = genie.params.ribbonCount ?? 5;
  for (let i = 0; i < n; i++) {
    ribbons.push({
      points: [{ x: width / 2, y: height / 2 }],
      offset: i * 0.3, hue: (i / n) * 360
    });
  }
  colorMode(HSB, 360, 100, 100, 100);
}
function draw() {
  background(0, 30);
  const ml = genie.params.maxLength ?? 80;
  const th = genie.params.thickness ?? 8;
  const spd = genie.params.speed ?? 0.1;
  const mx = mouseX || width / 2 + sin(frameCount * 0.02) * 100;
  const my = mouseY || height / 2 + cos(frameCount * 0.03) * 100;
  for (const r of ribbons) {
    const tx = mx + sin(frameCount * 0.03 + r.offset) * 40;
    const ty = my + cos(frameCount * 0.04 + r.offset) * 40;
    const head = r.points[0];
    const nx = lerp(head.x, tx, spd);
    const ny = lerp(head.y, ty, spd);
    r.points.unshift({ x: nx, y: ny });
    if (r.points.length > ml) r.points.pop();
    noFill();
    for (let i = 1; i < r.points.length; i++) {
      const t = i / r.points.length;
      const w = th * (1 - t);
      stroke((r.hue + frameCount * 0.5) % 360, 70, 100, (1 - t) * 80);
      strokeWeight(w);
      line(r.points[i - 1].x, r.points[i - 1].y, r.points[i].x, r.points[i].y);
    }
  }
}`,
  },
];
